import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiGovApi } from '../services/api';

const P  = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';

const TYPE_LABELS = {
  aup:               'Acceptable Use Policy',
  msa_addendum:      'MSA AI Addendum',
  governance_charter:'Governance Charter / RACI',
  governance_report: 'Governance Report',
  incident_response: 'Incident Response Addendum',
  tool_inventory:    'AI Tool Inventory Register',
  tool_intake_sop:   'AI Tool Intake / Approval SOP',
  hipaa_addendum:    'HIPAA AI Addendum',
  cmmc_addendum:     'CMMC / CUI AI Addendum',
  assessment_report: 'Assessment Report',
  cadence_review:    'Cadence Review / Meeting Notes',
  technical_report:  'Technical Report',
  training_record:   'Employee Training Attestation',
  vendor_dd:         'Vendor Due Diligence Checklist',
  risk_register:     'Risk Register',
  other:             'Other',
};

const APPLICABILITY_LABELS = {
  universal:       'Universal — all clients, all tiers',
  operations_up:   'Operations + Strategic tiers',
  strategic_only:  'Strategic tier only',
  healthcare:      'Healthcare clients (HIPAA-scoped)',
  defense_cmmc:    'Defense contractors (CMMC-scoped)',
  financial:       'Financial / SOX-scoped clients',
};

const TIER_MIN_LABELS = {
  '':           'Any tier',
  foundation:   'Foundation+',
  operations:   'Operations+',
  strategic:    'Strategic only',
};

const CATEGORY_OPTIONS = [
  'Generative AI',
  'Coding Assistant',
  'Image Generation',
  'Video Generation',
  'Audio / Speech',
  'Search / Research',
  'Productivity',
  'Data Analysis',
  'Other',
];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ── shared styles ──────────────────────────────────────────────────────────────
const primaryBtn = {
  padding: '8px 18px', backgroundColor: P, color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const outlineBtn = {
  padding: '8px 18px', backgroundColor: 'transparent', color: P,
  border: `2px solid ${P}`, borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(74,43,112,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalBox = {
  background: 'white', borderRadius: 12, padding: 28,
  boxShadow: '0 20px 60px rgba(74,43,112,0.2)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn  = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF' };
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: PD };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6,
  fontSize: 14, color: '#374151', boxSizing: 'border-box', outline: 'none',
};
const thStyle = {
  padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700,
  color: PD, textTransform: 'uppercase', letterSpacing: '0.05em',
};

// ── TemplatesTab ──────────────────────────────────────────────────────────────
function TemplatesTab() {
  const fileRef = useRef();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAll, setShowAll]     = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', type: 'aup', version: '1.0', description: '', applicability: 'universal', tier_min: '' });
  const [uploadFile, setUploadFile] = useState(null);

  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiGovApi.listTemplates(!showAll);
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showAll]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Select a .docx file'); return; }
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('name', uploadForm.name || uploadFile.name.replace('.docx', ''));
    fd.append('type', uploadForm.type);
    fd.append('version', uploadForm.version);
    fd.append('description', uploadForm.description);
    fd.append('applicability', uploadForm.applicability || 'universal');
    fd.append('tier_min', uploadForm.tier_min || '');
    setUploading(true);
    try {
      await aiGovApi.uploadTemplate(fd);
      toast.success('Template uploaded');
      setShowUpload(false);
      setUploadForm({ name: '', type: 'aup', version: '1.0', description: '', applicability: 'universal', tier_min: '' });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await aiGovApi.updateTemplate(editId, editForm);
      toast.success('Template updated');
      setEditId(null);
      load();
    } catch {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleScanFields = async () => {
    setScanning(true);
    try {
      const res = await aiGovApi.scanTemplateFields(editId);
      const fields = res.data.fields || [];
      setEditForm(f => ({ ...f, merge_fields: fields }));
      toast.success(`Found ${fields.length} merge fields`);
    } catch {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const updateFieldDef = (idx, key, val) => {
    setEditForm(f => {
      const mf = [...(f.merge_fields || [])];
      mf[idx] = { ...mf[idx], [key]: val };
      return { ...f, merge_fields: mf };
    });
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this template? It will no longer appear in document generation.')) return;
    try {
      await aiGovApi.deleteTemplate(id);
      toast.success('Template deactivated');
      load();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', flex: 1, marginRight: 16 }}>
          <strong style={{ color: '#92400E', fontSize: 13 }}>Auto-populated fields (use in .docx — any case):</strong>
          <div style={{ fontSize: 12, color: '#78350F', marginTop: 4, lineHeight: 1.8 }}>
            {['{{client_name}}','{{ir_contact_name}}','{{ir_contact_email}}','{{start_date}}','{{today}}','{{tier}}','{{document_version}}'].map(f => (
              <code key={f} style={{ background: '#FEF3C7', padding: '1px 5px', borderRadius: 3, marginRight: 6 }}>{f}</code>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280', cursor: 'pointer' }}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show inactive
          </label>
          <button onClick={() => setShowUpload(true)} style={primaryBtn}>+ Upload Template</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7E4DB3' }}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', color: '#9CA3AF' }}>
          No templates found.{' '}
          <button onClick={() => setShowUpload(true)} style={{ color: P, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            Upload the first one.
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PL }}>
                {['Name', 'Type', 'Applicability', 'Version', 'Status', 'Uploaded', ''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.map((t, i) => (
                <tr key={t.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA', opacity: t.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: PD }}>
                    {t.name}
                    {t.description && <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 400, marginTop: 2 }}>{t.description}</div>}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{TYPE_LABELS[t.type] || t.type}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {(() => {
                      const a = t.applicability || 'universal';
                      const colors = {
                        universal:      { bg: '#F3EEFA', color: '#4A2B70' },
                        operations_up:  { bg: '#DBEAFE', color: '#1E40AF' },
                        strategic_only: { bg: '#EDE9FE', color: '#5B21B6' },
                        healthcare:     { bg: '#FEF3C7', color: '#92400E' },
                        defense_cmmc:   { bg: '#FEE2E2', color: '#991B1B' },
                        financial:      { bg: '#D1FAE5', color: '#065F46' },
                      };
                      const c = colors[a] || colors.universal;
                      const tierLabel = t.tier_min ? ` · ${TIER_MIN_LABELS[t.tier_min] || t.tier_min}` : '';
                      return (
                        <span style={{ background: c.bg, color: c.color, borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {APPLICABILITY_LABELS[a]?.split(' — ')[0] || a}{tierLabel}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>v{t.version}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: t.is_active ? '#D1FAE5' : '#E5E7EB', color: t.is_active ? '#065F46' : '#6B7280', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>
                    {fmtDate(t.uploaded_at)}
                    {t.uploaded_by_name && <div style={{ fontSize: 11, color: '#9CA3AF' }}>by {t.uploaded_by_name}</div>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => {
                          setEditId(t.id);
                          let mf = t.merge_fields;
                          if (typeof mf === 'string') { try { mf = JSON.parse(mf); } catch { mf = []; } }
                          setEditForm({ name: t.name, description: t.description || '', version: t.version, is_active: t.is_active, merge_fields: mf || [], applicability: t.applicability || 'universal', tier_min: t.tier_min || '' });
                        }}
                        style={{ ...outlineBtn, padding: '4px 10px', fontSize: 12 }}
                      >
                        Edit / Fields
                      </button>
                      {t.is_active && (
                        <button onClick={() => handleDeactivate(t.id)} style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #FCA5A5', color: '#DC2626', background: 'white', borderRadius: 6, cursor: 'pointer' }}>
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Upload Template</h3>
              <button onClick={() => setShowUpload(false)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleUpload}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>File (.docx) *</label>
                  <input ref={fileRef} type="file" accept=".docx"
                    onChange={e => {
                      const f = e.target.files[0];
                      setUploadFile(f);
                      if (f && !uploadForm.name) setUploadForm(p => ({ ...p, name: f.name.replace('.docx', '') }));
                    }}
                    style={{ display: 'none' }}
                  />
                  <div onClick={() => fileRef.current.click()}
                    style={{ border: '2px dashed #E5D5F5', borderRadius: 8, padding: '16px', textAlign: 'center', cursor: 'pointer', background: uploadFile ? '#F0FDF4' : 'white' }}
                  >
                    {uploadFile
                      ? <span style={{ color: '#065F46', fontWeight: 600 }}>✓ {uploadFile.name}</span>
                      : <span style={{ color: '#9CA3AF' }}>Click to select a .docx file</span>
                    }
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Template Name *</label>
                  <input type="text" value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="e.g. AI Acceptable Use Policy" required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <select value={uploadForm.type} onChange={e => setUploadForm(p => ({ ...p, type: e.target.value }))} style={inputStyle}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Version</label>
                    <input type="text" value={uploadForm.version} onChange={e => setUploadForm(p => ({ ...p, version: e.target.value }))} style={inputStyle} placeholder="1.0" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Applicability</label>
                    <select value={uploadForm.applicability} onChange={e => setUploadForm(p => ({ ...p, applicability: e.target.value }))} style={inputStyle}>
                      {Object.entries(APPLICABILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Minimum Tier</label>
                    <select value={uploadForm.tier_min} onChange={e => setUploadForm(p => ({ ...p, tier_min: e.target.value }))} style={inputStyle}>
                      {Object.entries(TIER_MIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Optional description…" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setShowUpload(false)} style={outlineBtn} disabled={uploading}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editId && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 620 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Edit Template</h3>
              <button onClick={() => setEditId(null)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Version</label>
                    <input type="text" value={editForm.version} onChange={e => setEditForm(f => ({ ...f, version: e.target.value }))} style={inputStyle} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', cursor: 'pointer', paddingTop: 24 }}>
                    <input type="checkbox" checked={!!editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                    Active
                  </label>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Applicability</label>
                    <select value={editForm.applicability || 'universal'} onChange={e => setEditForm(f => ({ ...f, applicability: e.target.value }))} style={inputStyle}>
                      {Object.entries(APPLICABILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Minimum Tier</label>
                    <select value={editForm.tier_min || ''} onChange={e => setEditForm(f => ({ ...f, tier_min: e.target.value }))} style={inputStyle}>
                      {Object.entries(TIER_MIN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <div style={{ borderTop: '1px solid #E5D5F5', paddingTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Merge Field Definitions</label>
                    <button type="button" onClick={handleScanFields} disabled={scanning}
                      style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px' }}>
                      {scanning ? 'Scanning…' : '⟳ Scan Template'}
                    </button>
                  </div>
                  {(!editForm.merge_fields || editForm.merge_fields.length === 0) ? (
                    <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                      Click "Scan Template" to read placeholders from the .docx file.
                    </div>
                  ) : (
                    <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #E5D5F5', borderRadius: 6 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: PL, position: 'sticky', top: 0 }}>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Placeholder</th>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Label (shown to staff)</th>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: PD }}>Auto?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editForm.merge_fields.map((f, idx) => (
                            <tr key={idx} style={{ borderTop: '1px solid #F3EEFA', background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: PD, whiteSpace: 'nowrap' }}>
                                {'{{'}{f.key}{'}}'}
                              </td>
                              <td style={{ padding: '4px 10px' }}>
                                <input type="text" value={f.label || ''} onChange={e => updateFieldDef(idx, 'label', e.target.value)}
                                  style={{ width: '100%', border: '1px solid #E5D5F5', borderRadius: 4, padding: '3px 6px', fontSize: 12 }} />
                              </td>
                              <td style={{ padding: '6px 10px', textAlign: 'center', color: f.auto ? '#065F46' : '#9CA3AF', fontSize: 11, fontWeight: 600 }}>
                                {f.auto ? 'Auto' : 'Manual'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setEditId(null)} style={outlineBtn} disabled={saving}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TiersTab ──────────────────────────────────────────────────────────────────
function TiersTab() {
  const [tiers, setTiers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiGovApi.getTiers();
      setTiers(res.data.tiers || []);
    } catch { toast.error('Failed to load tiers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    let features = t.features;
    if (typeof features === 'string') { try { features = JSON.parse(features); } catch { features = []; } }
    if (!Array.isArray(features)) features = [];
    setEditForm({
      name:          t.name,
      monthly_price: t.monthly_price ?? '',
      annual_price:  t.annual_price ?? '',
      active:        t.active !== 0,
      features,
    });
    setEditId(t.id);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await aiGovApi.updateTier(editId, {
        ...editForm,
        monthly_price: editForm.monthly_price !== '' ? parseFloat(editForm.monthly_price) : null,
        annual_price:  editForm.annual_price  !== '' ? parseFloat(editForm.annual_price)  : null,
      });
      toast.success('Tier updated');
      setEditId(null);
      load();
    } catch { toast.error('Failed to save tier'); }
    finally { setSaving(false); }
  };

  const addFeature = () => setEditForm(f => ({ ...f, features: [...f.features, ''] }));
  const removeFeature = (i) => setEditForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const updateFeature = (i, val) => setEditForm(f => {
    const fs = [...f.features]; fs[i] = val; return { ...f, features: fs };
  });

  const fmtPrice = (p) => p != null ? `$${Number(p).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '—';

  if (loading) return <p style={{ color: '#6B7280', fontSize: 14 }}>Loading tiers…</p>;

  return (
    <div>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280' }}>
        Edit tier names, pricing, and the feature list shown to clients. Changes take effect immediately for new programs.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
        {tiers.map(t => {
          let features = t.features;
          if (typeof features === 'string') { try { features = JSON.parse(features); } catch { features = []; } }
          if (!Array.isArray(features)) features = [];
          return (
            <div key={t.id} style={{ border: `1px solid ${t.active !== 0 ? '#E5D5F5' : '#E5E7EB'}`, borderRadius: 10, overflow: 'hidden', opacity: t.active !== 0 ? 1 : 0.6 }}>
              <div style={{ background: t.active !== 0 ? PL : '#F9FAFB', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: PD }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#7E4DB3', fontFamily: 'monospace', marginTop: 2 }}>{t.code}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: PD }}>{fmtPrice(t.monthly_price)}<span style={{ fontWeight: 400, fontSize: 11, color: '#6B7280' }}>/mo</span></div>
                  {t.annual_price && <div style={{ fontSize: 11, color: '#6B7280' }}>{fmtPrice(t.annual_price)}/yr</div>}
                </div>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {features.length > 0 ? (
                  <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
                    {features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                ) : (
                  <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0, fontStyle: 'italic' }}>No features listed</p>
                )}
              </div>
              <div style={{ padding: '8px 16px 14px', borderTop: '1px solid #F3F4F6' }}>
                <button onClick={() => openEdit(t)} style={{ ...outlineBtn, fontSize: 12, padding: '4px 12px' }}>Edit</button>
                {t.active === 0 && <span style={{ marginLeft: 10, fontSize: 11, color: '#9CA3AF' }}>Inactive</span>}
              </div>
            </div>
          );
        })}
      </div>

      {editId && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>Edit Tier — {editForm.name}</h3>
              <button onClick={() => setEditId(null)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Display Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Monthly Price ($)</label>
                    <input type="number" min="0" step="1" value={editForm.monthly_price} onChange={e => setEditForm(f => ({ ...f, monthly_price: e.target.value }))} style={inputStyle} placeholder="0" />
                  </div>
                  <div>
                    <label style={labelStyle}>Annual Price ($)</label>
                    <input type="number" min="0" step="1" value={editForm.annual_price} onChange={e => setEditForm(f => ({ ...f, annual_price: e.target.value }))} style={inputStyle} placeholder="0" />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={labelStyle}>Features / What's Included</label>
                    <button type="button" onClick={addFeature} style={{ ...outlineBtn, fontSize: 11, padding: '2px 10px' }}>+ Add</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {editForm.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="text"
                          value={f}
                          onChange={e => updateFeature(i, e.target.value)}
                          style={{ ...inputStyle, flex: 1 }}
                          placeholder={`Feature ${i + 1}`}
                        />
                        <button type="button" onClick={() => removeFeature(i)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
                      </div>
                    ))}
                    {editForm.features.length === 0 && (
                      <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0, fontStyle: 'italic' }}>No features yet — click + Add</p>
                    )}
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} />
                  Active (visible in program enrollment)
                </label>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
                  <button type="button" onClick={() => setEditId(null)} style={{ ...outlineBtn, fontSize: 13, padding: '6px 16px' }}>Cancel</button>
                  <button type="submit" style={{ ...primaryBtn, fontSize: 13, padding: '6px 16px' }} disabled={saving}>{saving ? 'Saving…' : 'Save Tier'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ToolRegistryTab ───────────────────────────────────────────────────────────
function ToolRegistryTab() {
  const [tools, setTools]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAll, setShowAll]   = useState(false);
  const [search, setSearch]     = useState('');

  const [modalOpen, setModalOpen]     = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState({ name: '', vendor: '', category: '', description: '', domains: [] });
  const [domainInput, setDomainInput] = useState('');
  const [domainMode, setDomainMode]   = useState('dropdown'); // 'dropdown' | 'manual'
  const [observedDomains, setObservedDomains] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [syncing, setSyncing]         = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await aiGovApi.getToolRegistry(showAll);
      setTools(res.data.tools || []);
    } catch {
      toast.error('Failed to load tool registry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showAll]);

  const loadObservedDomains = async () => {
    try {
      const res = await aiGovApi.getObservedDomains();
      setObservedDomains(res.data.domains || []);
    } catch { /* non-fatal */ }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ name: '', vendor: '', category: '', description: '', domains: [] });
    setDomainInput('');
    setDomainMode('dropdown');
    setModalOpen(true);
    loadObservedDomains();
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    let domains = t.domains;
    if (typeof domains === 'string') { try { domains = JSON.parse(domains); } catch { domains = []; } }
    setForm({ name: t.name || '', vendor: t.vendor || '', category: t.category || '', description: t.description || '', domains: Array.isArray(domains) ? domains : [] });
    setDomainInput('');
    setDomainMode('dropdown');
    setModalOpen(true);
    loadObservedDomains();
  };

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) return;
    if (!form.domains.includes(d)) setForm(f => ({ ...f, domains: [...f.domains, d] }));
    setDomainInput('');
  };

  const removeDomain = (d) => setForm(f => ({ ...f, domains: f.domains.filter(x => x !== d) }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await aiGovApi.updateRegistryTool(editingId, form);
        toast.success('Tool updated');
      } else {
        await aiGovApi.createRegistryTool(form);
        toast.success('Tool added to registry');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (t) => {
    if (!window.confirm(`Deactivate "${t.name}"? It will no longer appear in client program tool selectors.`)) return;
    try {
      await aiGovApi.deactivateRegistryTool(t.id);
      toast.success('Tool deactivated');
      load();
    } catch {
      toast.error('Failed to deactivate');
    }
  };

  const handleRestore = async (t) => {
    try {
      await aiGovApi.restoreRegistryTool(t.id);
      toast.success('Tool restored');
      load();
    } catch {
      toast.error('Failed to restore');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await aiGovApi.syncToolRegistry();
      const { added, count } = res.data;
      if (count === 0) {
        toast.info('Registry already up to date — no new tools to add');
      } else {
        toast.success(`Added ${count} new tool${count !== 1 ? 's' : ''}: ${added.join(', ')}`);
        load();
      }
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = tools.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.name || '').toLowerCase().includes(q)
      || (t.vendor || '').toLowerCase().includes(q)
      || (t.category || '').toLowerCase().includes(q);
  });

  // Parse domains safely
  const parseDomains = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
    return [];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <div style={{ flex: 1, display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by name, vendor, category…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, maxWidth: 320 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show inactive
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSync} disabled={syncing} style={{ ...outlineBtn, fontSize: 13, padding: '7px 14px' }}>
            {syncing ? 'Syncing…' : '⟳ Sync from Catalog'}
          </button>
          <button onClick={openNew} style={primaryBtn}>+ Add Tool</button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
        {filtered.length} tool{filtered.length !== 1 ? 's' : ''} in registry
        {search && ` matching "${search}"`}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7E4DB3' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', color: '#9CA3AF' }}>
          No tools found.{' '}
          <button onClick={openNew} style={{ color: P, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Add the first one.</button>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PL }}>
                {['Tool', 'Vendor', 'Category', 'Domains', 'Status', ''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => {
                const domains = parseDomains(t.domains);
                return (
                  <tr key={t.id} style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA', opacity: t.is_active ? 1 : 0.55 }}>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: PD }}>
                      {t.name}
                      {t.description && <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 400, marginTop: 2 }}>{t.description}</div>}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151' }}>{t.vendor || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151' }}>
                      {t.category
                        ? <span style={{ background: PL, color: PD, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{t.category}</span>
                        : '—'
                      }
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {domains.length === 0
                          ? <span style={{ fontSize: 12, color: '#9CA3AF' }}>—</span>
                          : domains.map(d => (
                            <span key={d} style={{ background: '#EFF6FF', color: '#1E40AF', borderRadius: 4, padding: '1px 7px', fontSize: 11, fontFamily: 'monospace' }}>{d}</span>
                          ))
                        }
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ background: t.is_active ? '#D1FAE5' : '#E5E7EB', color: t.is_active ? '#065F46' : '#6B7280', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(t)} style={{ ...outlineBtn, padding: '4px 10px', fontSize: 12 }}>Edit</button>
                        {t.is_active ? (
                          <button onClick={() => handleDeactivate(t)} style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #FCA5A5', color: '#DC2626', background: 'white', borderRadius: 6, cursor: 'pointer' }}>
                            Deactivate
                          </button>
                        ) : (
                          <button onClick={() => handleRestore(t)} style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #6EE7B7', color: '#065F46', background: 'white', borderRadius: 6, cursor: 'pointer' }}>
                            Restore
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {modalOpen && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 18, fontWeight: 700 }}>{editingId ? 'Edit Tool' : 'Add Tool to Registry'}</h3>
              <button onClick={() => setModalOpen(false)} style={closeBtn}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Tool Name *</label>
                    <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. ChatGPT" required />
                  </div>
                  <div>
                    <label style={labelStyle}>Vendor</label>
                    <input type="text" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} style={inputStyle} placeholder="e.g. OpenAI" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                    <option value="">— Select category —</option>
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Optional short description…" />
                </div>

                {/* Domains */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Associated Domains</label>
                    <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid #E5D5F5' }}>
                      {['dropdown', 'manual'].map(m => (
                        <button key={m} type="button" onClick={() => { setDomainMode(m); setDomainInput(''); }}
                          style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
                            background: domainMode === m ? P : 'white', color: domainMode === m ? 'white' : '#6B7280' }}>
                          {m === 'dropdown' ? '≡ Pick from observed' : '✎ Enter manually'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Current domain chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 28 }}>
                    {form.domains.length === 0
                      ? <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>No domains added yet</span>
                      : form.domains.map(d => (
                        <span key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#1E40AF', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontFamily: 'monospace' }}>
                          {d}
                          <button type="button" onClick={() => removeDomain(d)} style={{ background: 'none', border: 'none', color: '#1E40AF', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                        </span>
                      ))
                    }
                  </div>

                  {domainMode === 'dropdown' ? (
                    (() => {
                      // Filter out already-added domains
                      const available = observedDomains.filter(d => !form.domains.includes(d));
                      return available.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6, fontStyle: 'italic' }}>
                          No unrecognized observed domains — switch to manual entry to add one.
                        </div>
                      ) : (
                        <div style={{ border: '1px solid #E5D5F5', borderRadius: 6, maxHeight: 180, overflowY: 'auto' }}>
                          {available.map(d => (
                            <div
                              key={d}
                              onClick={() => { setForm(f => ({ ...f, domains: [...f.domains, d] })); }}
                              style={{ padding: '6px 12px', fontSize: 12, fontFamily: 'monospace', color: '#1E40AF', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', background: 'white' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                              onMouseLeave={e => e.currentTarget.style.background = 'white'}
                            >
                              + {d}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        value={domainInput}
                        onChange={e => setDomainInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                        style={{ ...inputStyle, flex: 1 }}
                        placeholder="e.g. openai.com (Enter to add)"
                      />
                      <button type="button" onClick={addDomain} style={{ ...outlineBtn, padding: '8px 14px', whiteSpace: 'nowrap' }}>Add</button>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                    All root domains — subdomains match automatically (e.g. adding openai.com covers api.openai.com).
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => setModalOpen(false)} style={outlineBtn} disabled={saving}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add to Registry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AiGovernanceAdmin() {
  const navigate  = useNavigate();
  const [tab, setTab] = useState('templates');

  const tabs = [
    { key: 'templates', label: 'Document Templates' },
    { key: 'tools',     label: 'Tool Registry' },
    { key: 'tiers',     label: 'Service Tiers' },
  ];

  return (
    <div>
      <button onClick={() => navigate('/ai-gov')} style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
        ← AI Governance
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: PD, fontSize: 28, fontWeight: 700 }}>AI Governance Admin</h2>
          <p style={{ margin: '4px 0 0', color: '#7E4DB3', fontSize: 14 }}>
            Manage document templates and the global AI tool registry.
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '2px solid #E5D5F5', marginBottom: 24 }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              color: tab === t.key ? P : '#6B7280',
              borderBottom: tab === t.key ? `3px solid ${P}` : '3px solid transparent',
              marginBottom: -2,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'templates' && <TemplatesTab />}
      {tab === 'tools'     && <ToolRegistryTab />}
      {tab === 'tiers'     && <TiersTab />}
    </div>
  );
}
